import { ArticleFooterAuthorInfoProps } from "design-system/components/pages/article-page/article-footer/article-footer-author-info";
import { ArticleFooterLinkedAuthorProps } from "design-system/components/pages/article-page/article-footer/article-footer-linked-author";
import { ArticleFooterTagProps } from "design-system/components/pages/article-page/article-footer/article-footer-tag";
import {
  ArticlePage,
  ArticlePageTopper,
} from "design-system/components/pages/article-page/article-page";
import {
  ArticleRelatedPeople,
  RelatedPerson,
} from "design-system/components/pages/article-page/article-related-people/article-related-people";
import {
  ArticleShareTools,
  ArticleShareToolsProps,
  GetArticleShareToolsFooter,
} from "design-system/components/pages/article-page/article-share-tools/article-share-tools";
import {
  StickyEmailCta,
  StickyEmailCtaProps,
} from "design-system/components/pages/article-page/sticky-email-cta/sticky-email-cta";
import { Aside } from "design-system/components/primitives/aside/aside";
import { Footnotes } from "design-system/components/primitives/footnote/footnotes";
import { NewsletterSignupProps } from "design-system/components/primitives/newsletter-signup/newsletter-signup";
import { cloudinary } from "design-system/utils/cloudinary";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { documentToString } from "frontend/contentful/lib/document-to-string";
import { entriesToString } from "frontend/contentful/lib/entries-to-string";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { ArticlePageEntry } from "frontend/contentful/schema/pages";
import { TermEntry } from "frontend/contentful/schema/semantics";
import { EntryRef } from "frontend/contentful/schema/sys";
import { ParticipantStoryTopperEntry } from "frontend/contentful/schema/toppers";
import { useContentful } from "frontend/hooks/use-contentful";
import { usePageFootnotes } from "frontend/hooks/use-page-footnotes";
import { usePageNavigation } from "frontend/hooks/use-page-navigation";
import { usePageProps } from "frontend/hooks/use-page-props";
import { Fragment, ReactNode } from "react";
import {
  createArticleAuthorProps,
  createArticleLinkedAuthorProps,
} from "../primitives/article-author.props";
import { createArticleRelatedPersonProps } from "../primitives/article-related-person.props";
import { createBylineProps } from "../primitives/byline.props";
import { ContentfulHead } from "../primitives/head.contentful";
import { createMediaAssetProps } from "../primitives/media-asset.props";
import { createLazyTopperProps } from "../toppers/lazy-topper.props";
import { createSiteFooterProps } from "../universal/site-footer.props";
import { createSiteHeaderProps } from "../universal/site-header.props";
import { RenderContentfulEntry } from "../utils/render-contentful-entry";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";
import { ContentfulPageSections } from "./page-sections.contentful";

export const ContentfulArticlePage =
  createContentfulComponent<ArticlePageEntry>(
    ({ entry, createEditAttributes }) => {
      const {
        request,
        entry: pageRef,
        siteArticlePageSettings,
      } = usePageProps();
      const {
        getEntry,
        getEntryUrl,
        site,
        getEntries,
        getIsPersonPastHbsFaculty,
        getEntityName,
      } = useContentful();
      const { breadcrumbs } = usePageNavigation();
      const topper = createLazyTopperProps(entry.fields.topper, {
        parentPage: entry,
      }) as ArticlePageTopper;
      const footnotes = usePageFootnotes(entry.fields.body);
      const settings = getEntry(siteArticlePageSettings);
      const overlineSetting = settings?.fields.siteOverlineSettings;

      const topperEntry = getEntry(entry.fields.topper);
      const isParticipantStory = isContentType<ParticipantStoryTopperEntry>(
        "participantStoryTopper",
        topperEntry,
      );

      // Assign topper props from the article page entry
      if (topper?.props) {
        if (!isParticipantStory) {
          const title = entry?.fields.title;
          if (title) topper.props.title = title;
        }

        const subtitle = entry?.fields.description;
        if (subtitle) {
          topper.props.subtitle = (
            <RenderContentfulRichText document={subtitle} />
          );
        }
        const overline = getEntry(entry?.fields.overline);
        let overlineDefaultText: string | undefined = undefined;
        if (!overline && overlineSetting === "topic") {
          overlineDefaultText = entry.fields.topic?.length
            ? getEntry(entry.fields.topic[0])?.fields.title
            : undefined;
        } else if (!overline && overlineSetting === "category") {
          overlineDefaultText = entry.fields.category?.length
            ? getEntry(entry.fields.category[0])?.fields.title
            : undefined;
        }

        if (overline || overlineDefaultText) {
          topper.props.overline = {
            text: overline?.fields.text || overlineDefaultText,
            link: getEntryUrl(overline),
            editAttributes: {
              text: createEditAttributes({ entry: overline, fieldId: "text" }),
            },
          };
        }

        const byline = createBylineProps(entry);
        if (byline) topper.props.byline = byline;

        if (
          settings?.fields.authorsDisplay === "article footer only" ||
          settings?.fields.authorsDisplay === "hidden"
        ) {
          if (topper.props.byline) topper.props.byline.type = "DateOnly";
        }

        if (topper.props.byline) {
          const isSame =
            topper?.props?.byline?.authors?.length ===
              topper?.props?.byline?.featuredAuthors?.length &&
            topper?.props?.byline?.authors?.every((author) =>
              topper?.props?.byline?.featuredAuthors?.some(
                (featured) => featured.name === author.name,
              ),
            );

          if (isSame) {
            topper.props.byline.type = "Authors";
          }
        }

        const leadImageEntry = getEntry(entry?.fields.leadImage);
        if (leadImageEntry) {
          const mediaAsset = createMediaAssetProps(leadImageEntry);

          // force 1:1 aspect ratio for participant stories
          if (mediaAsset?.image && isParticipantStory) {
            mediaAsset.image.width = "400px";
            mediaAsset.image.height = "400px";
          }

          if (leadImageEntry?.fields.caption) {
            topper.props.mediaHighlightCaption = (
              <RenderContentfulRichText
                document={leadImageEntry.fields.caption}
              />
            );
          }

          topper.props.mediaAsset = mediaAsset;
        }

        topper.props.editAttributes = {
          ...topper.props.editAttributes,
          title: createEditAttributes({ entry: entry, fieldId: "title" }),
          subtitle: createEditAttributes({
            entry: entry,
            fieldId: "description",
          }),
          publicationDate: createEditAttributes({
            entry: entry,
            fieldId: "publicationDate",
          }),
        };
      }

      // Left rail
      const showSocialShareAside =
        settings?.fields.socialShareDisplay === "hidden" ||
        settings?.fields.socialShareDisplay === "article footer"
          ? false
          : true;

      const newsletterContent: NewsletterSignupProps = {
        title: "Never miss a story.",
        subtitle:
          "Get the latest insights from HBS experts in your inbox each week.",
        successTitle: "Thank you for signing up.",
        successSubtitle: "Watch your inbox for the latest insights from HBS.",
      };

      const newsletterProps: StickyEmailCtaProps = {
        heading:
          settings?.fields.showTaglineSidebar && settings.fields.taglineHeading
            ? settings.fields.taglineHeading
            : undefined,
        subheading:
          settings?.fields.showTaglineSidebar && settings.fields.tagline
            ? settings.fields.tagline
            : undefined,
        newsletterSignup: newsletterContent,
      };

      // Right rail
      const relatedPeople: Array<RelatedPerson> = [];
      const relatedFaculty: Array<RelatedPerson> = [];
      const relatedAlumni: Array<RelatedPerson> = [];
      const relatedStaff: Array<RelatedPerson> = [];
      const relatedNonHBS: Array<RelatedPerson> = [];
      const displayOrder: Array<ReactNode> = [];

      if (
        !settings?.fields.relatedPeopleDisplayed ||
        (settings?.fields.relatedPeopleDisplayed &&
          settings.fields.relatedPeopleDisplayed.includes("Related Faculty"))
      ) {
        entry.fields.relatedFaculty?.forEach((person) => {
          if (getIsPersonPastHbsFaculty(person)) return;
          const relatedPersonProps = createArticleRelatedPersonProps(person);
          if (relatedPersonProps) {
            relatedPeople.push(relatedPersonProps);
            relatedFaculty.push(relatedPersonProps);
          }
        });
      }
      if (settings?.fields.relatedPeopleDisplayed) {
        if (settings.fields.relatedPeopleDisplayed.includes("Related Alum")) {
          entry.fields.relatedAlum?.forEach((person) => {
            const relatedPersonProps = createArticleRelatedPersonProps(person);
            if (relatedPersonProps) {
              relatedPeople.push(relatedPersonProps);
              relatedAlumni.push(relatedPersonProps);
            }
          });
        }
        if (settings.fields.relatedPeopleDisplayed.includes("Related Staff")) {
          entry.fields.relatedStaff?.forEach((person) => {
            const relatedPersonProps = createArticleRelatedPersonProps(person);
            if (relatedPersonProps) {
              relatedPeople.push(relatedPersonProps);
              relatedStaff.push(relatedPersonProps);
            }
          });
        }
        if (
          settings.fields.relatedPeopleDisplayed.includes(
            "Related Non-HBS Person",
          )
        ) {
          entry.fields.relatedNonHbsPeople?.forEach((person) => {
            const relatedPersonProps = createArticleRelatedPersonProps(person);
            if (relatedPersonProps) {
              relatedPeople.push(relatedPersonProps);
              relatedNonHBS.push(relatedPersonProps);
            }
          });
        }
      }

      if (
        settings?.fields.separateRelatedPeopleDisplayed &&
        settings.fields.separateRelatedPeopleDisplayed !== "default"
      ) {
        if (settings.fields.separateRelatedPeopleDisplayed === "Related Alum") {
          if (relatedAlumni.length > 0) {
            displayOrder.push(
              <Aside title="Featured Alumni">
                <ArticleRelatedPeople people={relatedAlumni} />
              </Aside>,
            );
          }
          if (relatedFaculty.length > 0) {
            displayOrder.push(
              <Aside title="Featured Faculty">
                <ArticleRelatedPeople people={relatedFaculty} />
              </Aside>,
            );
          }
        } else {
          if (relatedFaculty.length > 0) {
            displayOrder.push(
              <Aside title="Featured Faculty">
                <ArticleRelatedPeople people={relatedFaculty} />
              </Aside>,
            );
          }
          if (relatedAlumni.length > 0) {
            displayOrder.push(
              <Aside title="Featured Alumni">
                <ArticleRelatedPeople people={relatedAlumni} />
              </Aside>,
            );
          }
        }
        if (relatedStaff.length > 0) {
          displayOrder.push(
            <Aside title="Featured Related Staff">
              <ArticleRelatedPeople people={relatedStaff} />
            </Aside>,
          );
        }
        if (relatedNonHBS.length > 0) {
          displayOrder.push(
            <Aside title="Featured Related Non-HBS Person">
              <ArticleRelatedPeople people={relatedNonHBS} />
            </Aside>,
          );
        }
      } else if (relatedPeople.length > 0) {
        displayOrder.push(
          <Aside
            title={
              entry.fields.relatedPeopleHeading ? (
                entry.fields.relatedPeopleHeading
              ) : settings?.fields.relatedPeopleHeading ? (
                <RenderContentfulRichText
                  document={settings.fields.relatedPeopleHeading}
                />
              ) : (
                "Featured Faculty"
              )
            }
            editAttributes={{
              title: createEditAttributes({
                entry,
                fieldId: "relatedPeopleHeading",
              }),
            }}
          >
            <ArticleRelatedPeople people={relatedPeople} />
          </Aside>,
        );
      }

      // Article footer
      const authors: Array<ArticleFooterAuthorInfoProps> = [];
      const linkedAuthors: Array<ArticleFooterLinkedAuthorProps> = [];

      const shouldHideAuthorsInFooter =
        settings?.fields.authorsDisplay === "article byline only" ||
        settings?.fields.authorsDisplay === "hidden";

      const shouldShowContactsInFooter =
        shouldHideAuthorsInFooter && settings?.fields.showContactsFooter;

      if (shouldShowContactsInFooter) {
        entry.fields.contacts?.forEach((contact) => {
          const person = getEntry(contact)?.fields.person;
          if (!person) {
            return;
          }

          if (settings?.fields.footerAuthorsDisplayStyle === "linked author") {
            const linkedAuthorProps = createArticleLinkedAuthorProps(person);
            if (linkedAuthorProps) linkedAuthors.push(linkedAuthorProps);
          } else {
            const authorProps = createArticleAuthorProps(person);
            if (authorProps) authors.push(authorProps);
          }
        });
      } else {
        entry.fields.authors?.forEach((author) => {
          if (settings?.fields.footerAuthorsDisplayStyle === "linked author") {
            const linkedAuthorProps = createArticleLinkedAuthorProps(author);
            if (linkedAuthorProps) linkedAuthors.push(linkedAuthorProps);
          } else {
            const authorProps = createArticleAuthorProps(author);
            if (authorProps) authors.push(authorProps);
          }
        });
        if (
          authors.length === 0 &&
          linkedAuthors.length === 0 &&
          entry.fields.legacyStoryBylineTypein
        ) {
          authors.push({ name: entry.fields.legacyStoryBylineTypein });
        }
      }

      const topicsList: Array<ArticleFooterTagProps> = [];
      entry.fields.topic?.forEach((topic) => {
        const topicTerm = getEntry(topic)?.fields.title;
        if (topicTerm) {
          topicsList.push({ label: topicTerm } as ArticleFooterTagProps);
        }
      });
      const showTopics =
        (settings?.fields.showTopicsFooter ?? topicsList.length > 0)
          ? true
          : false;

      const keywordsList: Array<ArticleFooterTagProps> = [];
      entry.fields.keywords?.forEach((keyword) => {
        const keywordTerm = getEntry(keyword)?.fields.keyword;
        if (keywordTerm) {
          keywordsList.push({ label: keywordTerm } as ArticleFooterTagProps);
        }
      });

      // default the aricle seo description to the article description
      const seo = getEntry(entry.fields.seo);

      let image = cloudinary(topper?.props?.mediaAsset?.image?.src);
      if (image) image = `https://${request.host}` + image;

      let url = getEntryUrl(getEntry(pageRef));
      if (url?.startsWith("/")) url = `https://${request.host}` + url;

      const extractTermEntryTitles = (entries?: Array<EntryRef<TermEntry>>) => {
        return (
          getEntries(entries)
            .map((i) => i?.fields?.title)
            .join(", ") || undefined
        );
      };

      return (
        <>
          <ContentfulHead
            entry={seo}
            description={
              seo?.fields.description ??
              documentToString(entry.fields.shortDescription) ??
              documentToString(entry.fields.description)
            }
            image={topper?.props?.mediaAsset?.image}
            articleStructuredData={{
              url,
              image,
              name: entry.fields.title,
              author: authors.length ? authors[0]?.name : undefined,
              datePublished: entry.fields.publicationDate,
              articleSection: topper?.props?.overline?.text,
              articleBody: entriesToString(getEntries(entry.fields.body)),
            }}
            pageLoadEventData={{
              /* eslint-disable camelcase */
              article_blog: {
                story_id: entry.sys.id,
                pub_date: entry.fields.publicationDate,
                authors: getEntries(entry.fields.authors)
                  .map(getEntityName)
                  .join(", "),
                story_type: extractTermEntryTitles(entry.fields.category),
                related_geo: extractTermEntryTitles(entry.fields.geography),
                related_topics: extractTermEntryTitles(entry.fields.topic),
                related_industry: extractTermEntryTitles(entry.fields.industry),
                related_faculty: getEntries(entry.fields.relatedFaculty)
                  .map(getEntityName)
                  .join(", "),
              },
              /* eslint-enable camelcase */
            }}
          />

          <ArticlePage
            theme={settings?.fields.colorThemeOverride}
            useDropCap={settings?.fields.useDropCap}
            breadcrumbs={breadcrumbs}
            siteHeader={{
              name: "SiteHeader",
              props: createSiteHeaderProps(site),
            }}
            siteFooter={createSiteFooterProps(site?.fields.footer)}
            topper={topper}
            body={{
              children: (
                <>
                  {entry.fields.body?.map((block, i) => (
                    <RenderContentfulEntry key={i} entry={block} />
                  ))}
                  {<Footnotes footnotes={footnotes} />}
                </>
              ),
              leftRail: settings?.fields.showNewsletterSidebar ? (
                <Aside>
                  <StickyEmailCta {...newsletterProps} />
                </Aside>
              ) : showSocialShareAside ? (
                <Aside>
                  <ArticleShareTools text={entry.fields.title || ""} />
                </Aside>
              ) : undefined,
              leftRailLabel: settings?.fields.showNewsletterSidebar
                ? "Newsletter sign up"
                : "Share Links",
              rightRail:
                displayOrder.length > 0
                  ? displayOrder.map((element, index) => (
                      <Fragment key={index}>{element}</Fragment>
                    ))
                  : undefined,
              rightRailLabel: settings?.fields.relatedPeopleHeading
                ? documentToString(settings.fields.relatedPeopleHeading)
                : "Featured Faculty",
            }}
            footer={{
              authors,
              linkedAuthors,
              hideAuthor:
                !shouldShowContactsInFooter && shouldHideAuthorsInFooter,
              tagsList: settings?.fields.showKeywordsFooter
                ? keywordsList
                : undefined,
              topicsList: showTopics ? topicsList : undefined,
              shareList:
                settings?.fields.socialShareDisplay === "article footer"
                  ? GetArticleShareToolsFooter({
                      text: entry.fields.title || "",
                    } as ArticleShareToolsProps)
                  : undefined,
              authorHeading: settings?.fields.footerAuthorsHeading ? (
                <RenderContentfulRichText
                  document={settings.fields.footerAuthorsHeading}
                  disableParagraphs
                />
              ) : shouldShowContactsInFooter ? (
                "Contacts"
              ) : undefined,
            }}
            preFooterContent={
              <ContentfulPageSections
                entry={entry?.fields.preFooterContent ? entry : settings}
                options={{ key: "preFooterContent" }}
              />
            }
          />
        </>
      );
    },
  );
